(function(){
  'use strict';

  // UTM/source capture: read utm_* params from the current URL once per page load and
  // persist them in sessionStorage (namespaced key) so later events in the same session -
  // e.g. add-to-cart/checkout happening pages after the shopper landed with UTM params -
  // can still be attributed. Falls back to document.referrer when no UTM params are
  // present and nothing has been captured yet this session.
  var UTM_STORAGE_KEY = 'fam_utm';
  var UTM_PARAM_NAMES = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content'];

  function captureUtm(){
    try{
      var params = new URLSearchParams(location.search);
      var found = {};
      var hasAny = false;
      UTM_PARAM_NAMES.forEach(function(key){
        var val = params.get(key);
        if(val){ found[key] = val; hasAny = true; }
      });
      if(hasAny){
        sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found));
        return;
      }
      if(!sessionStorage.getItem(UTM_STORAGE_KEY) && document.referrer){
        sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({referrer: document.referrer}));
      }
    }catch(e){}
  }
  captureUtm();

  function storedUtm(){
    try{
      var raw = sessionStorage.getItem(UTM_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    }catch(e){ return {}; }
  }

  // Generic analytics helper: pushes an event onto GA4/GTM's dataLayer and also
  // dispatches a matching DOM CustomEvent, in case any other script listens for it.
  // (Carried over from the old fam-agentic.js track() helper.) Now also merges in any
  // persisted UTM/referrer data on every call, so individual call sites never have to
  // remember to attach it themselves. Explicit fields in `detail` win on key collision.
  function track(name, detail){
    try{
      var merged = Object.assign({}, storedUtm(), detail || {});
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({event:name}, merged));
      document.dispatchEvent(new CustomEvent(name,{detail:merged}));
    }catch(e){}
  }
  // Exposed so inline section scripts (e.g. sections/s-redesign-purchase.liquid's own
  // selectType()) can fire real events through this same shared mechanism instead of
  // inventing a second tracking function.
  window.famTrack = track;

  function ready(fn){
    if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',fn);}else{fn();}
  }

  // Accessibility (WCAG baseline): visitors with prefers-reduced-motion set should not be
  // shown autoplay video. Pause and disarm any autoplay video already on the page.
  ready(function(){
    if(!window.matchMedia||!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('video[autoplay]').forEach(function(v){
      v.pause();
      v.removeAttribute('autoplay');
      v.loop=false;
      v.currentTime=0;
    });
  });

  // fam_page_view: fires once per page load. page/funnel/market/currency come from data
  // attributes rendered by layout/redesign.liquid's <body> tag (blank on layouts that
  // don't render them, e.g. the legacy layout/theme.liquid, which also loads this file).
  ready(function(){
    track('fam_page_view', {
      page: document.body.getAttribute('data-fam-page') || '',
      funnel: document.body.getAttribute('data-fam-page') || '',
      market: document.body.getAttribute('data-fam-market') || '',
      currency: document.body.getAttribute('data-fam-currency') || '',
      path: location.pathname
    });
  });

  // fam_need_selected: delegated (not an inline listener in the section itself) so it
  // works sitewide wherever .fam-need-card appears - currently the "Shop by need" cards
  // in sections/s-redesign-shop-by-need.liquid, which previously had no click handler at
  // all. Fires before navigation since dataLayer.push is synchronous.
  document.addEventListener('click', function(e){
    var card = e.target.closest('.fam-need-card');
    if(!card) return;
    var needEl = card.querySelector('.fam-need-tag');
    var productEl = card.querySelector('h3');
    track('fam_need_selected', {
      need: needEl ? needEl.textContent.trim() : '',
      product: productEl ? productEl.textContent.trim() : '',
      href: card.getAttribute('href') || ''
    });
  });

  // fam_purchase_module_view: fires once, the first time #purchase (the purchase module
  // in sections/s-redesign-purchase.liquid) actually enters the viewport - observer is
  // disconnected right after the first fire so re-scrolling never fires it again.
  ready(function(){
    var purchaseSection = document.getElementById('purchase');
    if(!purchaseSection || !('IntersectionObserver' in window)) return;
    var fired = false;
    var observer = new IntersectionObserver(function(entries){
      entries.forEach(function(entry){
        if(fired || !entry.isIntersecting) return;
        fired = true;
        var form = purchaseSection.querySelector('.fam-purchase-form');
        var variantInput = form ? form.querySelector('input[name="id"]') : null;
        track('fam_purchase_module_view', {
          product_handle: purchaseSection.getAttribute('data-fam-product-handle') || '',
          product_id: purchaseSection.getAttribute('data-fam-product-id') || '',
          variant_id: variantInput ? variantInput.value : ''
        });
        observer.disconnect();
      });
    }, { threshold: 0.3 });
    observer.observe(purchaseSection);
  });

  // fam_add_to_cart / fam_checkout_started: both purchase forms in this theme are plain
  // Shopify {% form %} POSTs with a full-page reload to /cart/add or /cart (no AJAX
  // cart/add fetch or cart-fetch intercept exists anywhere in this theme - confirmed by
  // searching every theme JS asset and section for "cart/add" and "fetch("; the only
  // fetch() in the theme is sections/s-redesign-cart.liquid's own /cart/change.js quantity
  // stepper, unrelated to add-to-cart/checkout). A `submit` listener, reading each form's
  // own real field values synchronously right before the browser navigates away, is the
  // reliable "real event" capture point for both - not the generic CTA click-proxy below,
  // which only ever fires fam_conversion_cta_click and never fired either of these events,
  // so there is nothing to remove from its selector list to avoid a double-fire.
  document.addEventListener('submit', function(e){
    var form = e.target;
    if(!form || typeof form.matches !== 'function') return;

    if(form.matches('.fam-purchase-form')){
      var section = form.closest('section');
      var variantInput = form.querySelector('input[name="id"]');
      var qtyInput = form.querySelector('input[name="quantity"]');
      var sellingPlanField = form.querySelector('[name="selling_plan"]:not(:disabled)');
      var sellingPlanId = (sellingPlanField && sellingPlanField.value) ? sellingPlanField.value : null;
      var priceEl = form.querySelector('[data-fam-btn-price]');
      track('fam_add_to_cart', {
        product_handle: section ? (section.getAttribute('data-fam-product-handle') || '') : '',
        product_id: section ? (section.getAttribute('data-fam-product-id') || '') : '',
        variant_id: variantInput ? variantInput.value : '',
        quantity: qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1,
        purchase_type: sellingPlanId ? 'subscribe' : 'once',
        selling_plan_id: sellingPlanId,
        market: document.body.getAttribute('data-fam-market') || '',
        currency: document.body.getAttribute('data-fam-currency') || '',
        price_display: priceEl ? priceEl.textContent.trim() : ''
      });
      return;
    }

    if(form.matches('.fam-checkout-form')){
      var subtotalCents = parseInt(form.getAttribute('data-fam-subtotal-cents'), 10) || 0;
      var itemCount = parseInt(form.getAttribute('data-fam-item-count'), 10) || 0;
      track('fam_checkout_started', {
        subtotal: subtotalCents / 100,
        currency: form.getAttribute('data-fam-currency') || document.body.getAttribute('data-fam-currency') || '',
        market: document.body.getAttribute('data-fam-market') || '',
        item_count: itemCount
      });
    }
  });

  // Conversion tracking: log a dataLayer event whenever a shopper clicks an
  // add-to-cart / checkout / primary CTA control, anywhere on the site. This listener
  // used to be nested inside the (now-removed) quiz drawer's init function and only
  // fired if that drawer's root element existed in the DOM, which it never did in this
  // theme -- it is unconditional here so it actually fires sitewide. Kept exactly as-is
  // and required per spec - fam_add_to_cart/fam_checkout_started fire only from the
  // submit listener above, never from here, so a purchase/checkout click firing both
  // this event and its own dedicated real event is expected, not a double-fire of the
  // SAME event.
  ready(function(){
    document.addEventListener('click',function(e){
      var cta=e.target.closest('a,button');
      if(!cta) return;
      if(cta.matches('.prod-add-btn,[name="checkout"],[data-cart],.fam-main-cta,.fam-buy-button,.fam-btn')){
        track('fam_conversion_cta_click',{label:(cta.textContent||'').trim().slice(0,80),path:location.pathname});
      }
    });
  });
})();
